import React, { cloneElement, ReactElement, ReactNode, memo } from "react";

import { useTheme } from "@emotion/react";
import {
  Browser,
  EtherscanLogo,
  Grid01,
  OpenseaLogo,
} from "@hl/base-components/lib/assets/icons.generated";
import { UTILITY_COLOR } from "@hl/base-components/lib/theme/colors";
import { envConfig } from "@hl/shared-features/lib/config";
import {
  buildTxUrl,
  getBlockExplorerName,
  networkLookup,
} from "@hl/shared-features/lib/utils/blockExplorer";
import { viewCollectionOpenSeaUrl } from "@hl/shared-features/lib/utils/opensea";
import {
  Anchor,
  Button,
  Title,
  Space,
  Image,
  Box,
  Group,
  Stack,
  createStyles,
  Center,
  SimpleGrid,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Link } from "react-router-dom";

import RevealPageCollectionImage from "assets/images/reveal-page-collection.png";
import { MINT_URL } from "~config";

import { GetSalePageCollectionQuery } from "../queries.graphql.generated";

const TokenRevealExplore = ({
  collection,
  isAnySeriesMint,
  txHash,
}: {
  collection: GetSalePageCollectionQuery["getPublicCollection"];
  isAnySeriesMint: boolean;
  txHash: string;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm - 1}px)`);

  const openSeaUrl =
    collection.openSeaCollectionUrl ||
    viewCollectionOpenSeaUrl(collection.address, collection.chainId);

  const network = networkLookup(collection.chainId).type;
  const explorerName = getBlockExplorerName(network);

  const explorerUrl = buildTxUrl(network, txHash);

  return (
    <>
      <SimpleGrid
        cols={isAnySeriesMint ? 4 : 3}
        spacing={40}
        breakpoints={[
          { maxWidth: "md", cols: 2 },
          { maxWidth: "sm", cols: 1 },
        ]}
      >
        <LinkBox
          icon={<Browser width={32} height={32} />}
          url={MINT_URL.tokens.mint.replace(":collectionId", collection.id)}
        >
          Go back to this collection’s mint page
        </LinkBox>
        {isAnySeriesMint && (
          <LinkBox
            icon={<Grid01 />}
            url={MINT_URL.tokens.browse.replace(":collectionId", collection.id)}
          >
            See what other people have minted from this collection
          </LinkBox>
        )}
        <LinkBox
          icon={<EtherscanLogo width={32} height={32} />}
          url={explorerUrl}
          anchor
        >
          View transaction on {explorerName}
        </LinkBox>
        <LinkBox icon={<OpenseaLogo />} url={openSeaUrl} anchor>
          View this collection on OpenSea
        </LinkBox>
      </SimpleGrid>
      <Group
        spacing={0}
        sx={{
          alignItems: "stretch",
          border: `1px solid ${theme.colors.divider[0]}`,
          overflow: "hidden", // Avoid covering the border
          [`@media (min-width: ${theme.breakpoints.md}px)`]: {
            flexWrap: "nowrap",
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            minWidth: 340,
            [`@media (min-width: ${theme.breakpoints.md}px)`]: {
              order: 2,
            },
          }}
          bg={UTILITY_COLOR.TABLE_ROW_BACKGROUND}
        >
          <Center p={isMobile ? 40 : 56}>
            <Image
              styles={{
                image: {
                  width: 564,
                  [theme.fn.smallerThan("xs")]: {
                    width: 340,
                  },
                  [theme.fn.smallerThan("sm")]: {
                    width: 368,
                  },
                },
              }}
              maw={564}
              src={RevealPageCollectionImage}
              fit="contain"
            />
          </Center>
        </Box>
        <Stack
          align="center"
          justify="center"
          sx={{
            padding: "40px 80px",
            [theme.fn.smallerThan("xs")]: {
              padding: 20,
            },
          }}
        >
          <Box>
            <Title order={isMobile ? 6 : 3}>
              Explore more great collections from top artists
            </Title>
            <Space h={48} />
            <Button component="a" size="xl" href={envConfig.home.baseUrl}>
              Explore collections
            </Button>
          </Box>
        </Stack>
      </Group>
    </>
  );
};

const useStyles = createStyles({
  link: {
    color: "inherit",
    textDecoration: "none",
  },
});

const LinkBox = ({
  icon,
  url,
  anchor = false,
  children,
}: {
  icon: ReactElement<{ width: number; height: number }>;
  url: string;
  anchor?: boolean;
  children: ReactNode;
}) => {
  const { classes, theme } = useStyles();

  const LinkContent = () => (
    <Stack justify="space-between" spacing={40}>
      {cloneElement(icon, { width: 32, height: 32 })}
      <Title order={6}>{children}</Title>
    </Stack>
  );

  return (
    <Box
      sx={{
        display: "flex",
        padding: "30px 40px",
        width: "100%",
        border: `1px solid ${theme.colors.divider[0]}`,
        [theme.fn.smallerThan("xs")]: {
          padding: 20,
        },
      }}
    >
      {anchor ? (
        <Anchor variant="text" href={url} target="_blank">
          {<LinkContent />}
        </Anchor>
      ) : (
        <Link className={classes.link} to={url}>
          {<LinkContent />}
        </Link>
      )}
    </Box>
  );
};

export default memo(TokenRevealExplore);
